import React, { useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom'

import { Sidebar, Menu, MenuItem, SubMenu, menuClasses } from 'react-pro-sidebar';
import { Profile } from '../../assets/icons/Profile';
import { Global } from '../../assets/icons/Global';
import { Geo } from '../../assets/icons/Geo';

import { SidebarHeader } from './helpers/SidebarHeader.jsx';
import { SidebarFooter } from './helpers/SidebarFooter.jsx';
import { Typography } from './helpers/Typography.jsx';
import { MobileView, isMobile } from 'react-device-detect';
import { Hamburger } from '../../assets/icons/Hamburger';
import { Reporting } from '../../assets/icons/Reports';
import { fetchTutorials } from '../../http/support.js';
import EyeTicket from '../Ui/EyeTicket/EyeTicket';
import SelectWithLabel from '../Inputs/Select.jsx';
import { Book } from '../../assets/icons/Book';
import { logout } from '../../http/auth.js';
import PrivateOrder from '../Ui/PrivateOrders/PrivateOrder.jsx';
import styled from 'styled-components';

import TutorialsModal from '../Ui/Support/TutorialsModal.jsx';
import { opticians, distributors, endUsers } from '../Helpers/userTypes.js';
import { Support } from '../../assets/icons/Support.jsx';

const MobileHeaderContainer = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background-color: ${({ bgColor }) => bgColor};
  padding: 10px 16px;
  z-index: 1000;

  @media (max-width: 768px) {
    padding: 8px 12px;
  }
`;

const HamburgerButton = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
`;

const themes = {
  light: {
    sidebar: {
      backgroundColor: '#ffffff',
      color: '#607489',
    },
    menu: {
      menuContent: '#fbfcfd',
      icon: '#0b3042',
      hover: {
        backgroundColor: '#c5e4ff',
        color: '#44596e',
      },
      disabled: {
        color: '#9fb6cf',
      },
    },
  },
  dark: {
    sidebar: {
      backgroundColor: '#333A58',
      color: '#BAC3CB',
    },
    menu: {
      menuContent: '#2D344F',
      icon: '#BAC3CB',
      hover: {
        backgroundColor: '#585F7C',
        color: '#b6c8d9',
      },
      disabled: {
        color: '#3e5e7e',
      },
    },
  },
};

const hexToRgba = (hex, alpha) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);

  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

export const NavBar = ({ backgroundColor = '#333A58', accountType, userEmail, account = null }) => {
  const [collapsed, setCollapsed] = React.useState(false);
  const [toggled, setToggled] = React.useState(false);
  const [rtl, setRtl] = React.useState(false);
  const [broken, setBroken] = React.useState(false);
  const [hasImage, setHasImage] = React.useState(false);
  const [theme, setTheme] = React.useState('dark');
  const [showEyeTicket, setShowEyeTicket] = React.useState(false);
  const [selectedTutorial, setSelectedTutorial] = React.useState(null);
  const [showOpticianPrivateOrder, setShowOpticianPrivateOrder] = React.useState(false);
  const [sightTest, setSightTest] = React.useState(false);
  const [expandedMenu, setExpandedMenu] = React.useState(null);
  const [tutorials, setTutorials] = React.useState([]);
  const [isLoggingOut, setIsLoggingOut] = React.useState(false);
  const navigate = useNavigate();

  themes.dark.sidebar.backgroundColor = backgroundColor.sidebar;
  themes.dark.menu.menuContent = backgroundColor.menuContent;
  themes.dark.menu.hover.backgroundColor = backgroundColor.hoverColour;

  themes.dark.sidebar.color = backgroundColor.font;
  themes.dark.menu.icon = backgroundColor.font;
  themes.dark.menu.hover.color = backgroundColor.font;

  useEffect(() => {;
    const fetchData = async () => {
      const tutorials = await fetchTutorials();
      setTutorials(tutorials);
    };

    fetchData();
  }, []);

  function ShowHideEyeTicket(sightTest = false, newTicketCreated = false) {
    setShowEyeTicket(!showEyeTicket);

    if (newTicketCreated) { navigate(`/tracking?ticketRaised=true`); }
  }

  function ShowHideTutorialModal(tutorial) {
    setSelectedTutorial(tutorial);
  }

  const handleMenuToggle = (menu) => {
    setExpandedMenu(expandedMenu === menu ? null : menu);
  };

  function ShowHidePrivateOrder(privateOrderCreated = false) {
    setShowOpticianPrivateOrder(!showOpticianPrivateOrder);

    if (privateOrderCreated) { navigate(0); }
  }

  const handleRTLChange = (e) => {
    setRtl(e.target.checked);
  };

  const handleThemeChange = (e) => {
    setTheme(e.target.checked ? 'dark' : 'light');
  };

  const handleImageChange = (e) => {
    setHasImage(e.target.checked);
  };

  const menuItemStyles = {
    root: {
      fontSize: '13px',
      fontWeight: 400,
    },
    icon: {
      color: themes[theme].menu.icon,
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
    },
    SubMenuExpandIcon: {
      color: '#b6b7b9',
    },
    subMenuContent: ({ level }) => ({
      backgroundColor:
        level === 0
          ? hexToRgba(themes[theme].menu.menuContent, hasImage && !collapsed ? 0.4 : 1)
          : 'transparent',
    }),
    button: {
      [`&.${menuClasses.disabled}`]: {
        color: themes[theme].menu.disabled.color,
      },
      '&:hover': {
        backgroundColor: hexToRgba(themes[theme].menu.hover.backgroundColor, hasImage ? 0.8 : 1),
        color: themes[theme].menu.hover.color,
      },
    },
    label: ({ open }) => ({
      fontWeight: open ? 600 : undefined,
    }),
  };

  const sidebarStyles = (isMobile ? {
    display: 'flex',
    height: '100vh',
    direction: rtl ? 'rtl' : 'ltr',
  } : {
    position: 'fixed',
    top: 0,
    left: 0,
    overflow: 'auto',
    display: 'flex',
    zIndex: 1001,
    height: '100vh',
    direction: rtl ? 'rtl' : 'ltr',
    boxShadow: '8px 0px 15px rgba(0, 0, 0, 0.2)'
  })

  return (
    <>
      {showOpticianPrivateOrder &&
        <PrivateOrder
          showHideModal={ShowHidePrivateOrder}
          isModalOpen={showOpticianPrivateOrder}
          colours={backgroundColor} />
      }
      {showEyeTicket &&
        <EyeTicket
          showHideModal={ShowHideEyeTicket}
          isModalOpen={showEyeTicket}
          colours={backgroundColor}
          sightTestOnly={sightTest}
          email={userEmail} />
      }
      {selectedTutorial &&
        <TutorialsModal
          showHideModal={ShowHideTutorialModal}
          isModalOpen={!!selectedTutorial}
          selectedTutorial={selectedTutorial}
          colour={backgroundColor}
        />
      }
      <MobileView>
        <MobileHeaderContainer bgColor={backgroundColor.sidebar}>
          <SidebarHeader rtl='rtl' title={backgroundColor.appName} isMobile={true} colour={backgroundColor.font} imageSrc={backgroundColor.image} style={{ padding: '0 0px' }} />
          <HamburgerButton onClick={() => setToggled(!toggled)}>
            <Hamburger size={40} />
          </HamburgerButton>
        </MobileHeaderContainer>
      </MobileView>
      <div style={{
        ...sidebarStyles
      }}>
        <Sidebar
          collapsed={collapsed}
          toggled={toggled}
          onBreakPoint={setBroken}
          onBackdropClick={() => setToggled(false)}
          rtl={rtl}
          breakPoint={isMobile ? 'lg' : 'md'}
          backgroundColor={hexToRgba(themes[theme].sidebar.backgroundColor, hasImage ? 0.9 : 1)}
          rootStyles={{
            color: themes[theme].sidebar.color,
          }}
        >
          <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
            <SidebarHeader rtl={rtl} title={backgroundColor.appName} colour={backgroundColor.font} imageSrc={backgroundColor.image} isMobile={isMobile} style={{ marginBottom: '24px', marginTop: '16px' }} />
            <div style={{ flex: 1, marginBottom: '32px' }}>
              <div style={{ padding: '0 24px', marginBottom: '8px' }}>
                <Typography
                  variant="body2"
                  fontWeight={600}
                  style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: '0.5px' }}
                >
                  Account
                </Typography>
              </div>
              <Menu menuItemStyles={menuItemStyles}>
                <SubMenu
                  label="Profile"
                  icon={<Profile />}
                  open={expandedMenu === 'profile'}
                  onOpenChange={() => handleMenuToggle('profile')}
                >
                  <MenuItem onClick={() => setToggled(false)} component={<Link to="/account" />}> Account Details</MenuItem>
                  <MenuItem onClick={() => setToggled(false)} component={<Link to="/preferences" />}> Preferences</MenuItem>
                </SubMenu>
                <div style={{ padding: '0 24px', marginBottom: '8px' }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: '0.5px' }}
                  >
                    Management
                  </Typography>
                </div>

                <SubMenu
                  label={!distributors.includes(accountType) ? "Settings & Reporting" : "Reports"}
                  icon={<Reporting />}
                  open={expandedMenu === 'settings'}
                  onOpenChange={() => handleMenuToggle('settings')}>
                  {(!distributors.includes(accountType) && <MenuItem onClick={() => setToggled(false)} component={<Link to="/configuration" />}>{opticians.includes(accountType) ? 'Fitting Frames' : 'EyeTicket™ Configuration'}</MenuItem>)}
                  <MenuItem onClick={() => setToggled(false)} component={<Link to="/reports" />}> Reporting</MenuItem>
                </SubMenu>
                <SubMenu
                  label="EyeTicket Management"
                  icon={<Global />}
                  open={expandedMenu === 'eyeticket'}
                  onOpenChange={() => handleMenuToggle('eyeticket')}>
                  {endUsers.includes(accountType) &&
                    <MenuItem
                      onClick={() => {
                        setSightTest(false);
                        setShowEyeTicket(true);
                      }}> Raise An EyeTicket™</MenuItem>
                  }
                  <MenuItem onClick={() => setToggled(false)} component={<Link to="/tracking" />}> EyeTicket™ History & Tracking</MenuItem>
                  {(distributors.includes(accountType) || accountType === "DirectUser" || accountType === "DirectUserEyeTicketOnly") && (
                    <MenuItem onClick={() => setToggled(false)} component={<Link to="/authorisetickets" />}>
                      Authorise EyeTicket™ Orders
                    </MenuItem>
                  )}

                  {(!distributors.includes(accountType) && <>
                    {(opticians.includes(accountType) && <MenuItem onClick={() => setToggled(false)} component={<Link to="/awaitingprocessing" />}> EyeTicket™ Processing</MenuItem>)}
                    {(!opticians.includes(accountType) && account && account.sighttest_paid === 2) &&
                      <MenuItem onClick={() => {
                        setSightTest(true);
                        setShowEyeTicket(true);
                      }}>
                        EyeTicket™ Sight Test Only</MenuItem>}
                    {opticians.includes(accountType) && <MenuItem onClick={() => setToggled(false)} component={<Link to="/opticianclinics" />}> My EyeTicket™ Clinics</MenuItem>}
                    {!opticians.includes(accountType) && <MenuItem onClick={() => setToggled(false)} component={<Link to="/clinics" />}> My EyeTicket™ Clinics</MenuItem>}
                    {!opticians.includes(accountType) && <MenuItem onClick={() => setToggled(false)} component={<Link to="/testhistory" />}>  Sight Test Information</MenuItem>}
                  </>
                  )}
                </SubMenu>
                {opticians.includes(accountType) && accountType !== "Opticiandispeningonly" && <>
                  <div style={{ padding: '0 24px', marginBottom: '8px' }}>
                    <Typography
                      variant="body2"
                      fontWeight={600}
                      style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: '0.5px' }}
                    >
                      Private Opticians Module
                    </Typography>
                  </div>
                  <SubMenu
                    label="Private Orders"
                    icon={<Book />}
                    open={expandedMenu === 'privateOrders'}
                    onOpenChange={() => handleMenuToggle('privateOrders')}>
                    <MenuItem onClick={() => setToggled(false)} component={<Link to="/privateorders" />}> Private Order History & Tracking</MenuItem>
                    <MenuItem onClick={() => setShowOpticianPrivateOrder(true)}> Private RX Online Order</MenuItem>
                  </SubMenu>
                </>
                }
                <div style={{ padding: '0 24px', marginBottom: '8px' }}>
                  <Typography
                    variant="body2"
                    fontWeight={600}
                    style={{ opacity: collapsed ? 0 : 0.7, letterSpacing: '0.5px' }}
                  >
                    Help & Support
                  </Typography>
                </div>

                {tutorials?.length > 0 && (
                <SubMenu
                  label="Video Tutorials"
                  icon={<Support />}
                  open={expandedMenu === 'videoTutorials'}
                  onOpenChange={() => handleMenuToggle('videoTutorials')}>
                    {
                      tutorials.map((tutorial, index) => (
                        <MenuItem 
                          key={index} 
                          onClick={() => ShowHideTutorialModal(tutorial)}
                          title={tutorial.tutorial_name}
                          >
                          {tutorial.tutorial_name}
                        </MenuItem>
                      ))
                    }
                </SubMenu>)}
              </Menu>
            </div>
            <SidebarFooter colours={backgroundColor} setToggled={setToggled} deleteCookie={logout} />
          </div>
        </Sidebar>
      </div>
    </>
  );
};
